import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { PlusIcon, DownloadIcon } from '../components/Icons';
import { EventModal } from './Events';
import GuestDetailsModal from '../components/GuestDetailsModal';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { logoBase64 } from '../logo';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import './Events.css';

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}

function statusClass(pass) {
  if (pass.status === 'used') return 'badge-success';
  if (pass.status === 'expired') return 'badge-danger';
  if (pass.status === 'revoked') return 'badge-secondary';
  if (pass.isPassGenerated === false) return 'badge-info';
  return 'badge-warning';
}

function statusLabel(pass) {
  if (pass.status === 'used') return 'Arrived';
  if (pass.status === 'expired') return 'Expired';
  if (pass.status === 'revoked') return 'Revoked';
  if (pass.isPassGenerated === false) return 'Registered';
  return 'Pending';
}

/* ─────────────────────────────────────────────────────────────
   EventRow – accordion row with inline details + guest list
───────────────────────────────────────────────────────────── */
function EventRow({ event, isOpen, onToggle, faculty, isOwner, onEdit, onDelete }) {
  const [passes, setPasses] = useState([]);
  const [loadingPasses, setLoadingPasses] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const isSuperAdmin = faculty?.role === 'super_admin';

  // Coordinator: populated object > logged-in user (only for own events)
  const coordinator =
    event?.faculty && typeof event.faculty === 'object' && event.faculty.name
      ? event.faculty
      : isOwner
        ? faculty
        : {};

  // Check if current user is the primary host or an admin
  const isAdmin = ['iqac', 'super_admin'].includes(faculty?.role);
  const isAO = faculty?.role === 'ao';
  const isPrimaryHost = event?.faculty && (
    typeof event.faculty === 'object'
      ? String(event.faculty._id) === String(faculty?._id)
      : String(event.faculty) === String(faculty?._id)
  );

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventEnd = event.isMultiDay && event.endDate ? new Date(event.endDate) : new Date(event.date);
  const endDay = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
  const isLocked = today > endDay;

  const canEdit = (isAdmin || isPrimaryHost) && (!isLocked || isAdmin);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingPasses(true);
    api.get('/passes', { params: { eventId: event._id, withNoPass: 'true' } })
      .then(res => setPasses(res.data.passes || []))
      .catch(() => setPasses([]))
      .finally(() => setLoadingPasses(false));
  }, [isOpen, event._id]);

  const handleApprove = async () => {
    try {
      setLoadingPasses(true);
      await api.post(`/events/${event._id}/approve`);
      // Reload is handled by parent or by just updating local state if we had it
      // But best is to reload events in parent
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve');
    } finally {
      setLoadingPasses(false);
    }
  };

  const guestCount = passes.filter(p => p.status !== 'revoked').length;

  return (
    <>
      <div
        className={`event-accordion-row ${isOpen ? 'is-open' : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={ev => ev.key === 'Enter' && onToggle()}
      >
        <div className="event-date-block">
          <div className="event-date-day" style={event.isMultiDay ? { fontSize: '0.92rem' } : {}}>
            {event.isMultiDay && event.endDate ? (
              (() => {
                const start = new Date(event.date);
                const end = new Date(event.endDate);
                if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
                  return `${start.getDate()}-${end.getDate()}`;
                }
                return `${start.getDate()}…${end.getDate()}`;
              })()
            ) : new Date(event.date).getDate()}
          </div>
          <div className="event-date-month" style={event.isMultiDay && event.endDate && new Date(event.date).getMonth() !== new Date(event.endDate).getMonth() ? { fontSize: '0.55rem', letterSpacing: '0' } : {}}>
            {(() => {
              const start = new Date(event.date);
              const end = event.endDate ? new Date(event.endDate) : null;
              const startMonth = start.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
              if (event.isMultiDay && end && start.getMonth() !== end.getMonth()) {
                const endMonth = end.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
                return `${startMonth}-${endMonth}`;
              }
              return startMonth;
            })()}
          </div>
        </div>
        <div className="event-accordion-main">
          <div className="event-accordion-title">{event.title}</div>
          <div className="event-preview-meta">
            <span>{event.venue}</span>
            <span className="dot">·</span>
            <span style={{ textTransform: 'capitalize', fontWeight: 600, color: 'var(--navy)', display: 'flex', alignItems: 'center' }}>
              {event.eventMode || 'Offline'}
              {event.isMultiDay && event.endDate && (
                <span style={{ marginLeft: '0.5rem', background: '#eff6ff', color: '#1e40af', padding: '1px 8px', borderRadius: '6px', fontSize: '0.7rem', border: '1px solid #dbeafe' }}>
                  {new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(event.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </span>
              )}
            </span>
            <span className="dot">·</span>
            <span>Host: {coordinator.name || 'Faculty'}</span>
            <span className="dot">·</span>
            <span>Guests: {event.passCount || 0}</span>
          </div>
        </div>
        <div className="event-accordion-right">
          <div className="event-type-badge">{event.type}</div>
          {canEdit && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ padding: '0.25rem 0.65rem', fontSize: '0.75rem' }}
              onClick={e => { e.stopPropagation(); onEdit(event); }}
            >
              Edit
            </button>
          )}
          <div className={`event-accordion-chevron ${isOpen ? 'rotated' : ''}`}>›</div>
        </div>
      </div>

      {isOpen && (
        <div className="event-accordion-panel">
          {/* Event Details */}
          <div className="event-inline-details">
            <div className="event-inline-hero">
              <div className="event-inline-title">{event.title}</div>
              <span className="event-type-badge" style={{ fontSize: '0.7rem' }}>{event.type}</span>
            </div>

            <div className="event-detail-grid" style={{ marginTop: '1rem' }}>
              <div className="pass-detail-item"><span className="pass-detail-label">Venue</span><span className="pass-detail-value">{event.venue || '—'}</span></div>
              <div className="pass-detail-item"><span className="pass-detail-label">Mode</span><span className="pass-detail-value" style={{ textTransform: 'capitalize' }}>{event.eventMode || 'Offline'}</span></div>
              <div className="pass-detail-item">
                <span className="pass-detail-label">Date</span>
                <span className="pass-detail-value" style={{ fontWeight: 700, color: 'var(--navy)' }}>
                  {event.isMultiDay && event.endDate ? (
                    <>
                      {new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      {' - '}
                      {new Date(event.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </>
                  ) : formatDate(event.date)}
                </span>
              </div>
              <div className="pass-detail-item"><span className="pass-detail-label">Guests</span><span className="pass-detail-value">{guestCount}</span></div>
              <div className="pass-detail-item"><span className="pass-detail-label">Host</span><span className="pass-detail-value">{coordinator.name || '—'}</span></div>
              <div className="pass-detail-item"><span className="pass-detail-label">Host email</span><span className="pass-detail-value">{coordinator.email || '—'}</span></div>
            </div>

            {/* Collaborators list */}
            {event.collaborators?.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <div style={{ fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--navy)', marginBottom: '1rem' }}>
                  Collaborators ({event.collaborators.length})
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                  {event.collaborators.map(c => (
                    <div key={c._id} style={{ padding: '0.875rem 1rem', background: 'rgba(0,95,167,0.05)', borderRadius: '14px', border: '1px solid rgba(0,95,167,0.12)' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-1)' }}>{c.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: '0.125rem' }}>{c.department && c.department !== 'General' && c.department !== 'University Faculty' ? c.department : ''}</div>
                      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', fontSize: '0.75rem' }}>
                        <span style={{ color: 'var(--navy)', fontWeight: 600 }}>{c.email}</span>
                        {c.phone && <span style={{ color: 'var(--text-3)' }}>{c.phone}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Approval Status Card Removed - All events are auto-approved */}
          </div>

          {/* Guest List */}
          <div className="event-guest-list" style={{ marginTop: '2.5rem' }}>
            <div style={{ fontWeight: 800, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-2)', marginBottom: '1rem' }}>
              Guest list ({passes.length})
            </div>
            {loadingPasses ? (
              <div style={{ padding: '1.5rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : passes.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-3)', fontSize: 'var(--text-sm)' }}>No guests issued yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {passes.map((pass, index) => (
                  <div
                    key={pass._id}
                    className="guest-preview-card"
                    onClick={() => setSelectedPass(pass)}
                  >
                    <div className="guest-preview-avatar">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                    <div className="guest-preview-content">
                      <div className="guest-preview-name">{pass.guestName || '—'}</div>
                      <div className="guest-preview-meta">
                        <div style={{ fontWeight: 600, color: 'var(--text-2)' }}>{pass.profession || ''}</div>
                        <div>{pass.organization || ''}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedPass && (
        <GuestDetailsModal pass={selectedPass} onClose={() => setSelectedPass(null)} />
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   EventList – renders a filtered, searchable accordion list
───────────────────────────────────────────────────────────── */
function EventList({ events, faculty, search, isOwner, openEventId, setOpenEventId, onEdit, onDelete, emptyMsg, filterType, filterMode }) {
  const filtered = events.filter(e => {
    // 1. Search filter
    const matchesSearch = search.trim() ? (
      e.title.toLowerCase().includes(search.toLowerCase()) ||
      e.venue?.toLowerCase().includes(search.toLowerCase()) ||
      e.type?.toLowerCase().includes(search.toLowerCase())
    ) : true;

    // 2. Type filter
    const matchesType = filterType === 'all' || e.type === filterType;

    // 3. Mode filter
    const matchesMode = filterMode === 'all' || e.eventMode === filterMode;

    return matchesSearch && matchesType && matchesMode;
  }).sort((a, b) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getEndDate = (e) => {
      const start = new Date(e.date);
      return e.isMultiDay && e.endDate ? new Date(e.endDate) : start;
    };

    const endA = getEndDate(a);
    const endB = getEndDate(b);
    
    const isFinishedA = endA < today;
    const isFinishedB = endB < today;

    // Move finished events to the bottom
    if (isFinishedA && !isFinishedB) return 1;
    if (!isFinishedA && isFinishedB) return -1;
    
    // Within same group, sort by date descending (latest first)
    return new Date(b.date) - new Date(a.date);
  });

  if (filtered.length === 0) {
    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-3)' }}>
          <div style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
            {search ? 'No matching events' : 'No events found'}
          </div>
          <div style={{ fontSize: '0.9375rem', marginTop: '0.5rem' }}>{!search && emptyMsg}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {filtered.map((e, idx) => (
        <div key={e._id} style={{ borderBottom: idx < filtered.length - 1 ? '1px solid var(--border-1)' : 'none' }}>
          <EventRow
            event={e}
            isOpen={openEventId === e._id}
            onToggle={() => setOpenEventId(openEventId === e._id ? null : e._id)}
            faculty={faculty}
            isOwner={isOwner}
            onEdit={onEdit}
            onDelete={onDelete}
            filterType={filterType}
            filterMode={filterMode}
          />
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   IQACEvents page
───────────────────────────────────────────────────────────── */
export default function IQACEvents() {
  const { faculty } = useAuth();
  const [myEvents, setMyEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [openEventId, setOpenEventId] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterMode, setFilterMode] = useState('all');

  const isAdminOrAO = ['iqac', 'super_admin', 'ao'].includes(faculty?.role);
  const isSuperAdmin = faculty?.role === 'super_admin';
  const isAO = faculty?.role === 'ao';
  const pageSubtitle = isAdminOrAO ? 'Manage university events' : 'Manage your personal events';

  const [showExportMenu, setShowExportMenu] = useState(false);

  const exportExcel = () => {
    const eventsToExport = activeTab === 'my' ? myEvents : (activeTab === 'upcoming' ? allEvents.filter(e => new Date(e.isMultiDay && e.endDate ? e.endDate : e.date) >= new Date().setHours(0, 0, 0, 0)) : allEvents.filter(e => new Date(e.isMultiDay && e.endDate ? e.endDate : e.date) < new Date().setHours(0, 0, 0, 0)));
    const data = eventsToExport.map((e, idx) => ({
      'Sno': idx + 1,
      'Event Title': e.title,
      'Venue': e.venue || '—',
      'Date': formatDate(e.date),
      'End Date': e.endDate ? formatDate(e.endDate) : '—',
      'Mode': e.eventMode || 'Offline',
      'Type': e.type || '—',
      'Host': (typeof e.faculty === 'object' ? e.faculty?.name : '—') || '—',
      'Guests Count': e.passCount || 0
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Events');
    XLSX.writeFile(wb, `University_Events_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel report exported');
  };

  const exportPDF = async () => {
    const toastId = toast.loading('Generating official events audit report...');
    const eventsToExport = activeTab === 'my' ? myEvents : (activeTab === 'upcoming' ? allEvents.filter(e => new Date(e.isMultiDay && e.endDate ? e.endDate : e.date) >= new Date().setHours(0, 0, 0, 0)) : allEvents.filter(e => new Date(e.isMultiDay && e.endDate ? e.endDate : e.date) < new Date().setHours(0, 0, 0, 0)));

    const ITEMS_PER_PAGE = 8;
    const chunks = [];
    for (let i = 0; i < eventsToExport.length; i += ITEMS_PER_PAGE) {
      chunks.push(eventsToExport.slice(i, i + ITEMS_PER_PAGE));
    }

    const pdf = new jsPDF('l', 'mm', 'a4');

    try {
      for (let pageIdx = 0; pageIdx < chunks.length; pageIdx++) {
        const currentChunk = chunks[pageIdx];
        const exportRoot = document.createElement('div');
        exportRoot.style.position = 'absolute';
        exportRoot.style.top = '0';
        exportRoot.style.left = '-2000px';
        exportRoot.style.width = '1100px';
        exportRoot.style.backgroundColor = '#ffffff';
        exportRoot.style.padding = '30px';
        exportRoot.style.fontFamily = "'Plus Jakarta Sans', sans-serif";

        exportRoot.innerHTML = `
          <div style="border: 1px solid #e2e8f0; border-radius: 8px; height: 710px; display: flex; flex-direction: column; overflow: hidden;">
            <!-- Header -->
            <div style="padding: 20px 30px; border-bottom: 3px solid #005fa7; margin-bottom: 20px;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
                <img src="${logoBase64}" style="height: 60px; object-fit: contain;" />
                <div style="text-align: right;">
                  <div style="text-transform: uppercase; font-size: 10px; font-weight: 800; color: #005fa7; letter-spacing: 2px;">Official Document</div>
                  <div style="font-size: 28px; font-weight: 900; color: #0f172a;">Events Audit Report</div>
                </div>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: flex-end;">
                <div>
                  <p style="color: #64748b; margin: 0; font-size: 14px; font-weight: 500;">
                    University Administrative Copy &nbsp;&bull;&nbsp; Date: ${new Date().toLocaleDateString('en-IN')}
                  </p>
                </div>
                <div style="text-align: right;">
                  <div style="font-size: 12px; color: #64748b; font-weight: 600;">Page ${pageIdx + 1} of ${chunks.length}</div>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div style="flex-grow: 1; padding: 0 30px;">
              <table style="width: 100%; border-collapse: separate; border-spacing: 0; font-size: 11px;">
                <thead>
                  <tr>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700; border-radius: 6px 0 0 0;">S.NO</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">EVENT TITLE</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">VENUE</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">DATE</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">MODE</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700;">TYPE</th>
                    <th style="padding: 15px 10px; background: #005fa7; color: #ffffff; text-align: center; font-weight: 700; border-radius: 0 6px 0 0;">GUESTS</th>
                  </tr>
                </thead>
                <tbody>
                  ${currentChunk.map((e, i) => `
                    <tr style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #64748b; font-weight: 600; text-align: center;">${String(pageIdx * ITEMS_PER_PAGE + i + 1).padStart(2, '0')}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #0f172a; font-weight: 700; text-align: center;">
                        ${e.title}
                        <div style="font-size: 9px; font-weight: 400; color: #64748b; margin-top: 2px;">Host: ${(typeof e.faculty === 'object' ? e.faculty?.name : '—') || '—'}</div>
                      </td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #334155; text-align: center;">${e.venue || '—'}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #334155; text-align: center;">${formatDate(e.date)}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #0f172a; font-weight: 600; text-align: center; text-transform: capitalize;">${e.eventMode || 'Offline'}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #64748b; text-align: center;">${e.type || '—'}</td>
                      <td style="padding: 12px 10px; border-bottom: 1px solid #f1f5f9; color: #005fa7; font-weight: 700; text-align: center;">${e.passCount || 0}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>

            <!-- Footer -->
            <div style="padding: 20px 30px; border-top: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; margin-top: auto;">
              <div style="font-size: 10px; color: #94a3b8;">
                &copy; ${new Date().getFullYear()} Guest Portal KRMU. All rights reserved.
              </div>
              <div style="font-size: 10px; color: #94a3b8; font-style: italic;">
                Events Inventory Copy - Page ${pageIdx + 1}
              </div>
            </div>
          </div>
        `;

        document.body.appendChild(exportRoot);
        await new Promise(r => setTimeout(r, 400));
        const canvas = await html2canvas(exportRoot, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
        document.body.removeChild(exportRoot);
        const imgData = canvas.toDataURL('image/png');
        if (pageIdx > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, 297, 210);
      }
      pdf.save(`Events_Audit_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Official events report exported', { id: toastId });
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Failed to generate PDF', { id: toastId });
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showExportMenu && !e.target.closest('.export-dropdown-wrap')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const loadData = useCallback(() => {
    setLoading(true);
    const endpoints = isAdminOrAO ? [api.get('/events'), api.get('/events/all')] : [api.get('/events')];
    Promise.all(endpoints)
      .then(([myRes, allRes]) => {
        setMyEvents(myRes.data.events || []);
        if (allRes) {
          setAllEvents(allRes.data.events || []);
        }
      })
      .catch(err => console.error('Error loading events:', err))
      .finally(() => setLoading(false));
  }, [isAdminOrAO]);

  const handleDelete = async (ev) => {
    try {
      const res = await api.delete(`/events/${ev._id}`);
      if (res.data.success) {
        toast.success('Event deleted');
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete event');
    }
  };

  useEffect(() => { loadData(); }, [loadData]);

  // Set default tab for non-admin/non-AO - MUST be before any conditional returns
  useEffect(() => {
    if (!isAdminOrAO && activeTab !== 'my') {
      setActiveTab('my');
    }
  }, [isAdminOrAO, activeTab]);

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const upcomingAll = allEvents.filter(e => {
    // If it's multi-day, stay in upcoming until the endDate has passed
    const eventEnd = e.isMultiDay && e.endDate ? new Date(e.endDate) : new Date(e.date);
    const eventEndDay = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
    return eventEndDay >= todayStart;
  }).sort((a, b) => new Date(a.date) - new Date(b.date));

  const conductedAll = allEvents.filter(e => {
    const eventEnd = e.isMultiDay && e.endDate ? new Date(e.endDate) : new Date(e.date);
    const eventEndDay = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
    return eventEndDay < todayStart;
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const TABS = isAdminOrAO ? [
    { key: 'upcoming', label: `Upcoming (${upcomingAll.length})` },
    { key: 'conducted', label: `Conducted (${conductedAll.length})` },
    { key: 'my', label: `My Events (${myEvents.length})` },
  ] : [
    { key: 'my', label: `My Events (${myEvents.length})` }
  ];

  const currentEvents =
    activeTab === 'my' ? myEvents :
      activeTab === 'upcoming' ? upcomingAll :
        conductedAll;

  const emptyMsgs = {
    my: 'Create a new event to get started',
    upcoming: 'No upcoming events in the university',
    conducted: 'No events have been conducted yet',
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Events</div>
          <div className="page-subtitle">{pageSubtitle}</div>
        </div>
        <div className="page-actions" style={{ gap: '0.75rem', alignItems: 'center' }}>
          {!isAO && (
            <button type="button" className="btn btn-navy" onClick={() => { setEditEvent(null); setShowEventModal(true); }}>
              + Create event
            </button>
          )}

          <div className="export-dropdown-wrap" style={{ position: 'relative' }}>
            <button
              className="btn btn-ghost"
              onClick={() => setShowExportMenu(!showExportMenu)}
              style={{ width: '42px', height: '42px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 900 }}
            >
              ⋮
            </button>
            {showExportMenu && (
              <div className="card shadow-lg" style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: '180px',
                zIndex: 100,
                padding: '0.5rem',
                border: '1px solid var(--border-1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', padding: '0.5rem 0.75rem' }}>Export Options</div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => { exportExcel(); setShowExportMenu(false); }}
                  style={{ justifyContent: 'flex-start', width: '100%', fontSize: '0.8125rem' }}
                >
                  <DownloadIcon style={{ width: '1rem', height: '1rem' }} />
                  Excel Report
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => { exportPDF(); setShowExportMenu(false); }}
                  style={{ justifyContent: 'flex-start', width: '100%', fontSize: '0.8125rem' }}
                >
                  <DownloadIcon style={{ width: '1rem', height: '1rem' }} />
                  PDF Report
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="card toolbar-fixed" style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center', background: 'white', padding: '0.75rem 1rem', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
          <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
            <div style={{ position: 'absolute', left: '1rem', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </div>
            <input
              className="toolbar-input-clean"
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 3rem',
                borderRadius: '12px',
                border: '1px solid transparent',
                background: '#f8fafc',
                fontSize: '0.9375rem',
                color: '#1e293b',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              placeholder="Search by name, venue or type…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                background: 'white',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#475569',
                cursor: 'pointer',
                minWidth: '130px'
              }}
            >
              <option value="all">All Types</option>
              <option value="meetup">Meetup</option>
              <option value="seminar">Seminar</option>
              <option value="workshop">Workshop</option>
              <option value="conference">Conference</option>
              <option value="guest_lecture">Guest Lecture</option>
              <option value="interview">Interview</option>
              <option value="other">Other</option>
            </select>

            <select
              value={filterMode}
              onChange={e => setFilterMode(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                background: 'white',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#475569',
                cursor: 'pointer',
                minWidth: '130px'
              }}
            >
              <option value="all">All Modes</option>
              <option value="offline">Offline</option>
              <option value="online">Online</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-container" style={{ marginBottom: '1rem' }}>
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              className={`tab-button ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => { setActiveTab(t.key); setOpenEventId(null); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Events Accordion */}
        <EventList
          events={currentEvents}
          faculty={faculty}
          search={search}
          filterType={filterType}
          filterMode={filterMode}
          isOwner={activeTab === 'my'}
          openEventId={openEventId}
          setOpenEventId={setOpenEventId}
          onEdit={ev => { setEditEvent(ev); setShowEventModal(true); }}
          onDelete={handleDelete}
          emptyMsg={emptyMsgs[activeTab]}
        />

        {showEventModal && (
          <EventModal
            event={editEvent}
            onClose={() => { setShowEventModal(false); setEditEvent(null); }}
            onSave={() => { setShowEventModal(false); setEditEvent(null); loadData(); }}
            onDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
}
